import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import router from "routes";
import { get } from "util/requests";
import colors from "colors";
import moment from "moment-timezone";
import styles from "./crf.module.css";
import LoadingButton from "@mui/lab/LoadingButton";
import { CheckIcon } from "ui/icons";
import Tooltip from "@mui/material/Tooltip";
import { DataIcon, DataIconBlue } from "ui/icons";
import strings from "strings";
import { OpenQueryIcon, RespondedQueryIcon, ResolvedQueryIcon } from "ui/icons";
import { arrayHaveCommonItems } from "util/helpers";
import { useSelector } from "react-redux";

export const renderFlagsRow = (row, query = null, permissions = []) => {
  let show_query = arrayHaveCommonItems(permissions, [
    "start_query",
    "resolve_query",
    "respond_query",
    "view_query_status",
  ]);

  return (
    <div className={styles.icon_wrapper}>
      {show_query && query?.status === "open" ? (
        <div className={styles.query_icon_wrapper}>
          <Tooltip title={strings.query_open} key={1}>
            <OpenQueryIcon fontSize="16px" />
          </Tooltip>
        </div>
      ) : null}
      {show_query && query?.status === "responded" ? (
        <div className={styles.query_icon_wrapper}>
          <Tooltip title={strings.query_responded} key={1}>
            <RespondedQueryIcon fontSize="16px" />
          </Tooltip>
        </div>
      ) : null}
      {show_query && query?.status === "resolved" ? (
        <div className={styles.query_icon_wrapper}>
          <Tooltip title={strings.query_resolved} key={1}>
            <ResolvedQueryIcon fontSize="16px" />
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("all_site_signoff") ? (
        <div className={styles.all_icon}>
          <Tooltip title={strings.crf_site_signoff}>S</Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("monitored") &&
      !row.flags?.includes("all_monitored") &&
      !row.flags?.includes("remonitored") ? (
        <div className={styles.partial_icon}>
          <Tooltip title={strings.partial_monitored_flag} key={1}>
            M
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("all_monitored") && !row.flags?.includes("remonitored") ? (
        <div className={styles.all_icon}>
          <Tooltip title={strings.all_fields_monitored} key={1}>
            M
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("remonitored") ? (
        <div className={styles.re_icon}>
          <Tooltip title={strings.re_monitor} key={1}>
            M
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("qa") && !row.flags?.includes("all_qa") && !row.flags?.includes("reqa") ? (
        <div className={styles.partial_icon}>
          <Tooltip title={strings.partial_qa_flag} key={2}>
            Q
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("all_qa") && !row.flags?.includes("reqa") ? (
        <div className={styles.all_icon}>
          <Tooltip title={strings.all_qa_flag} key={1}>
            Q
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("reqa") ? (
        <div className={styles.re_icon}>
          <Tooltip title={strings.re_qa} key={1}>
            Q
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("all_data") ? (
        <div>
          <Tooltip title={strings.all_data_flag} key={1}>
            <div className={styles.data_icon_wrapper}>
              <DataIconBlue fontSize={"16px"} />
            </div>
          </Tooltip>
        </div>
      ) : null}
      {row.flags?.includes("data") && !row.flags.includes("all_data") ? (
        <div>
          <Tooltip title={strings.partial_data_flag} key={1}>
            <div className={styles.data_icon_wrapper}>
              <DataIcon fontSize={"16px"} />
            </div>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

export const ae_columns = [
  {
    field: "term",
    headerName: "AE Term",
    width: 300,
    align: "left",
    headerAlign: "center",
    renderCell: ({ row }) => (row.term?.length > 30 ? row.term.slice(0, 40) + "..." : row.term || ""),
  },
  {
    field: "serious",
    headerName: "Serious",
    width: 80,
    align: "center",
    headerAlign: "center",

    renderCell: ({ row }) => (
      <div className={styles.icon_wrapper}>{row.serious === "yes" ? <CheckIcon fontSize="18px" /> : ""}</div>
    ),
  },
  {
    field: "start_date",
    headerName: "Start Date",
    width: 150,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "open_queries",
    headerName: "Open Queries",
    width: 150,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "flags",
    headerName: "Flags",
    width: 150,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => renderFlagsRow(row),
  },
  {
    field: "safety_review",
    headerName: "SAE Adjudication",
    width: 150,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => renderFlagsRow({ flags: row.safety_review || [] }),
  },
  {
    field: "meddra",
    headerName: "MedDRA",
    width: 150,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (row.meddra ? <CheckIcon fontSize="18px" /> : ""),
  },

  {
    field: "created_on",
    headerName: `Created On (${moment.tz(moment.tz.guess()).zoneAbbr()})`,
    width: 200,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (row.created_on && moment(row.created_on).format("yyyy-MMM-DD HH:mm:ss")) || "",
  },
  {
    field: "modified_on",
    headerName: `Modified On (${moment.tz(moment.tz.guess()).zoneAbbr()})`,
    width: 200,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (row.modified_on && moment(row.modified_on).format("yyyy-MMM-DD HH:mm:ss")) || "",
  },
];

export function LogFormTable(props) {
  const { crf_id, subject_id, domain_id, updateCrfStatus, form_type, multi_entry_crf_id, data } = props;
  const permissions = useSelector((state) => state.user.userData.permissions);

  const default_columns = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "details",
      headerName: "Details",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        let details = row.details;
        if (details) {
          try {
            details = JSON.parse(details);
            details = details.join(", ");
          } catch (err) {
            details = "";
          }
        }
        return details;
      },
    },
    {
      field: "created_on",
      headerName: "Created On",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (row.created_on && moment(row.created_on).format("yyyy-MMM-DD HH:mm:ss")) || "",
    },
    {
      field: "modified_on",
      headerName: "Modified On",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (row.modified_on && moment(row.modified_on).format("yyyy-MMM-DD HH:mm:ss")) || "",
    },
    {
      field: "flags",
      headerName: "Flags",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => renderFlagsRow(row, row.query, permissions),
    },
  ];

  function getColumns() {
    if (crf_id === "adverse_events") {
      return ae_columns;
    } else {
      return default_columns;
    }
  }
  return (
    <DataGrid
      rows={data.forms}
      columns={getColumns()}
      disableColumnSelector
      disableRowSelectionOnClick
      disableColumnMenu
      pageSize={10}
      rowsPerPageOptions={[10]}
      hideFooterSelectedRowCount
      pageSizeOptions={[10]}
      columnHeaderHeight={34}
      rowHeight={34}
      autoPageSize
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
      }}
      sx={{
        boxShadow: 0,
        border: 1,
        borderColor: "#f0f0f0",
        "& .MuiDataGrid-cell:hover": {
          color: "#8349fd",
          cursor: "pointer",
        },
        "& .MuiDataGrid-topContainer .MuiDataGrid-columnHeader ": {
          // backgroundColor: "#f7f3fe",
          backgroundColor: colors.table_header_background,
        },
        "& .MuiDataGrid-topContainer .MuiDataGrid-filler ": {
          // backgroundColor: "#f7f3fe",
          backgroundColor: colors.table_header_background,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 700,
          color: "#333",
        },
      }}
      onRowClick={(row, event) => {
        let link = `/subjects/${subject_id}/edc/visit/1/multi_entry/${crf_id}/${row.id}`;
        if (event.metaKey || event.ctrlKey) {
          let new_win = window.open(link, "_blank");
          if (new_win && !new_win.closed) {
            new_win.focus();
          }
        } else {
          router.navigate(link);
        }
      }}
      components={{
        NoRowsOverlay: () => <div className={styles.no_rows_overlay}>No CRF Data</div>,
      }}
    />
  );
}

export default function LogFormTableWrapper(props) {
  const { crf_id, subject_id, visit_index } = props;
  const [queries, setQueries] = useState([]);
  const [crfName, setCrfName] = useState("");
  const [data, setData] = useState({ forms: [] });

  useEffect(() => {
    get(`/crf_name_from_id/${crf_id}`).then((ret) => setCrfName(ret.data.name));
  }, [crf_id]);

  useEffect(() => {
    get(`/multi_entry_crfs/${subject_id}/${crf_id}`).then((ret) => setData(ret.data));
  }, [crf_id, subject_id]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.subject_title_wrapper}>
        <div className={styles.subject_title}>
          <strong>{props.subject_id}</strong>
        </div>
      </div>
      {data?.site_name ? <div className={styles.site_title2}>{`${data?.site_name} (${data?.site_code})`}</div> : ""}
      <div>
        <div className={styles.stretch_header}>
          <div>
            <strong>{crfName && crfName.toUpperCase()}</strong>
          </div>
          {data.write_permission ? (
            <div>
              <LoadingButton
                variant="contained"
                loading={false}
                onClick={() =>
                  router.navigate(
                    `/subjects/${subject_id}/edc/visit/${visit_index}/multi_entry/${crf_id}/${crypto.randomUUID()}`
                  )
                }
              >
                <strong>ADD NEW</strong>
              </LoadingButton>
            </div>
          ) : null}
        </div>
      </div>

      <LogFormTable {...props} data={data} />
    </div>
  );
}
